import { StreamLanguage } from "@codemirror/language"
import { shell } from "@codemirror/legacy-modes/mode/shell"
import { styled } from "@linaria/react"
import { useNavigate, useRouter } from "@tanstack/react-router"
import { observer } from "mobx-react-lite"
import { useState } from "react"

import { Badge } from "@/components/Badge/Badge.tsx"
import { Button } from "@/components/Button/Button.tsx"
import { CodeMirror } from "@/components/CodeMirror/CodeMirror.tsx"
import { Dialog } from "@/components/Dialog/Dialog.tsx"
import { EllipsisContainer } from "@/components/Ellipsis/Ellipsis.ts"
import { Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { Heading2 } from "@/components/Heading/Heading.tsx"
import { Principal } from "@/components/Principal.tsx"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { CheckmarkMedium, CodeFileLight, Icon12, Icon32 } from "@/icons/Icons.ts"
import { formatDateTime } from "@/lib/date.ts"
import { openLinkInNewWindow } from "@/lib/std.ts"
import { Navigator } from "@/routes/Navigator.ts"
import { useCurrentOrgId } from "@/routes/projects/projectHooks.ts"
import { TaskViewId } from "@/routes/tasks/TaskViewId.ts"
import { TabId as TaskTabId } from "@/routes/tasks/TaskViewScene/TaskTabId.ts"
import { ellipsis, focusOutline } from "@/styles/mixins.ts"
import { ellipsize, smallestFontSize, smallFontSize } from "@/styles/textStyles.ts"

import type { ProjectPreviewSetupViewModel } from "@/components/ProjectSettingsDialog/ProjectPreviewSetupViewModel.ts"


export const ProjectPreviewSetupTab = observer((props: {
  model: ProjectPreviewSetupViewModel
}) => {
  const router = useRouter()
  const navigate = useNavigate()
  const orgId = useCurrentOrgId()

  const vm = props.model

  const [showWarmup, setShowWarmup] = useState(false)
  const [showPreviewIndex, setShowPreviewIndex] = useState<number | null>(null)

  const doSetupAgain = async () => {
    const task = await vm.setupAgain()
    const view = props.model.runtimeConfig.get() ? TaskViewId.Full : TaskViewId.Chat
    await navigate(Navigator.task(orgId, vm.projectKey, task.id, { view, tab: TaskTabId.RuntimeConfigFile }))
  }

  if (vm.isLoading.get()) {
    return (
      <Vertical gap="16px">
        <Heading2>Preview Setup</Heading2>

        <Spinner text={ellipsize("Loading")} />
      </Vertical>
    )
  }

  return (
    <Vertical gap="16px">
      <Heading2>Preview Setup</Heading2>

      {!vm.isReadonly.get() && vm.runtimeConfigPresent.get() && vm.canEditProject.get() && (
        <Horizontal gap="12px">
          <Button kind="primary" disabled={vm.setupInProgress.get()} onClick={doSetupAgain}>
            {vm.setupInProgress.get() ? ellipsize("Setting up") : "Set up again"}
          </Button>
        </Horizontal>
      )}

      <ScriptsRow>
        {vm.warmupScript && (
          <ScriptCard onClick={() => setShowWarmup(true)}>
            <CodeFileLight className={Icon32} color="var(--secondary-text-color)" />
            <Vertical alignItems="flex-start" gap="4px">
              <EllipsisContainer><ScriptTitle>Warmup Script</ScriptTitle></EllipsisContainer>
              <ScriptSubtitle>Click to view</ScriptSubtitle>
            </Vertical>
          </ScriptCard>
        )}
        {vm.previewScripts.map((cmd, idx) => (
          <ScriptCard key={cmd.id} onClick={() => setShowPreviewIndex(idx)}>
            <CodeFileLight className={Icon32} color="var(--secondary-text-color)" />
            <Vertical alignItems="flex-start" gap="4px">
              <EllipsisContainer><ScriptTitle>{cmd.title}</ScriptTitle></EllipsisContainer>
              <ScriptSubtitle>Click to view</ScriptSubtitle>
            </Vertical>
          </ScriptCard>
        ))}
      </ScriptsRow>

      {vm.isReadonly.get() && (
        <ReadonlyLabel>
          Matter project configuration is defined in the runtime config file stored in the repository's default branch
          and can't be modified here.
        </ReadonlyLabel>
      )}

      {!vm.isReadonly.get() && vm.tasks.get().length > 0 && (
        <Vertical gap="16px">
          <TasksList>
            {vm.tasks.get().map(task => {
              const successful = task.successful === true
              const readonly = !!task.readonly
              const kind = readonly ? "disabled" : successful ? "success" : "info"
              const label = (successful ? "Successful" : readonly ? "Incomplete" : "In progress")
              const title = task.title || (task.initRuntimeConfigState ? "Preview configuration" : task.initProjectState ? "Project initialization" : "Idea")
              const dateText = formatDateTime(task.createdAt)

              const openTask = () => {
                const view = props.model.runtimeConfig.get() ? TaskViewId.Full : TaskViewId.Chat
                const url = router.buildLocation(Navigator.task(orgId, vm.projectKey, task.id, {
                  view,
                  tab: TaskTabId.RuntimeConfigFile
                })).href
                openLinkInNewWindow(url)
              }

              return (
                <TaskCard key={task.id} onClick={openTask}>
                  <TaskCardRow>
                    <TaskTitle>{title}</TaskTitle>

                    <Spring />

                    <Badge kind={kind} variant="small" textTransform="uppercase">
                      {successful && <CheckmarkMedium className={Icon12} />}
                      {label}
                    </Badge>

                    {readonly && <Badge kind="disabled" variant="small" textTransform="uppercase">Readonly</Badge>}
                  </TaskCardRow>

                  <TaskCardRow>
                    <TaskCardSecondary>
                      <Principal principal={task.createdBy} stopPropagation />
                    </TaskCardSecondary>

                    <Spring />

                    <TaskCardSecondary>{dateText}</TaskCardSecondary>
                  </TaskCardRow>
                </TaskCard>
              )
            })}
          </TasksList>
        </Vertical>
      )}

      {!!vm.warmupScript && (
        <Dialog
          open={showWarmup}
          onOpenChange={setShowWarmup}
          title="Warmup Script"
          trigger={null}
          maxWidth="100%"
        >
          <CodeMirror
            value={vm.warmupScript}
            onChange={() => {
            }}
            readOnly
            basicSetup={{ foldGutter: false, highlightActiveLine: false, highlightActiveLineGutter: false }}
            extensions={[StreamLanguage.define(shell)]}
          />
        </Dialog>
      )}

      {showPreviewIndex !== null && vm.previewScripts[showPreviewIndex] && (
        <Dialog
          open
          onOpenChange={(open) => setShowPreviewIndex(open ? showPreviewIndex : null)}
          title={vm.previewScripts[showPreviewIndex].title}
          trigger={null}
          maxWidth="100%"
        >
          <CodeMirror
            value={vm.previewScripts[showPreviewIndex].command}
            onChange={() => {
            }}
            readOnly
            basicSetup={{ foldGutter: false, highlightActiveLine: false, highlightActiveLineGutter: false }}
            extensions={[StreamLanguage.define(shell)]}
          />
        </Dialog>
      )}
    </Vertical>
  )
})

const ScriptsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`

const ScriptCard = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  flex: 1 1 0;
  min-width: 0;

  padding: 8px;

  border: 1px solid var(--separator-color);
  border-radius: var(--border-radius-12);

  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--text-color-5);
  }

  &:focus-visible {
    ${focusOutline};
  }
`

const ScriptTitle = styled.div`
  color: var(--text-color);

  ${smallFontSize};
  ${ellipsis};
`

const ScriptSubtitle = styled.div`
  color: var(--secondary-text-color);

  ${smallestFontSize};
`

const TasksList = styled(Vertical)`
  gap: 8px;
`

const TaskCard = styled(Vertical)`
  gap: 4px;

  padding: 8px 16px;
  border-radius: var(--border-radius-12);
  background: var(--text-color-3);
  cursor: pointer;

  &:hover {
    background: var(--text-color-5);
  }
`

const TaskCardRow = styled(Horizontal)`
  align-items: center;
  gap: 8px;
`

const TaskCardSecondary = styled(Horizontal)`
  color: var(--secondary-text-color);

  ${smallestFontSize};
`

const TaskTitle = styled.div`
  color: var(--text-color);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;

  ${ellipsis};

  max-width: 100%;
`

const ReadonlyLabel = styled.div`
  color: var(--secondary-text-color);

  ${smallFontSize};
`
